import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { style } from 'typestyle';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import CachedIcon from 'mdi-react/CachedIcon';
import { useAdminOrAuthorOffices } from './hooks/offices.hook';
import OfficeParkingsGroup from '../parkings/groups/office-parkings-group.component';
import OfficeOverviewGroup from './groups/office-overview-group.component';
import { useParkings } from '../parkings/hooks/parkings.hook';
import useFacilities from '../facilities/hooks/facilities.hooks';
import { IsMobileContext } from '../../core/context/is-mobile';
import CommonStyles from '../../core/common.styles';
import OfficeFacilitiesGroup from '../facilities/groups/office-facilities-group.component';
import { useKeycloak } from '../authentication/keycloak/KeycloakProvider';
import AppConstants from '../../core/app.constants';
import { dataCache } from '../../core/utils/data-cache';
import * as listStyles from '../../core/admin-list.styles';
import * as surfaceStyles from '../../core/admin-surface.styles';
import * as tabStyles from './office-tabs.styles';

type OverviewTabKey = 'offices' | 'parkings' | 'facilities';

const OVERVIEW_TAB_KEYS: OverviewTabKey[] = ['offices', 'parkings', 'facilities'];

const OfficeOverview = (): React.JSX.Element => {
  const { t } = useTranslation();
  const keycloak = useKeycloak();

  // Incrementing this triggers a forced cache flush + re-fetch in all data hooks.
  const [cacheVersion, setCacheVersion] = useState(0);

  const officesState = useAdminOrAuthorOffices(cacheVersion);
  const parkingState = useParkings(cacheVersion);
  const facilityState = useFacilities(cacheVersion);
  const isMobile = useContext(IsMobileContext);
  const { offices } = officesState;
  const { parkings } = parkingState;
  const { facilities } = facilityState;

  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab = useMemo<OverviewTabKey>(() => {
    const requested = searchParams.get('tab');
    return OVERVIEW_TAB_KEYS.includes(requested as OverviewTabKey) ? (requested as OverviewTabKey) : 'offices';
  }, [searchParams]);

  // Tabs lazy-mount on first visit and then stay mounted, hidden via display:none, so switching
  // back keeps each panel's search, filters and page intact (same technique as the statistics
  // page and the office detail tabs).
  const [visitedTabs, setVisitedTabs] = useState<Set<OverviewTabKey>>(() => new Set([activeTab]));
  useEffect(() => {
    setVisitedTabs((current) => (current.has(activeTab) ? current : new Set(current).add(activeTab)));
  }, [activeTab]);

  const setTab = useCallback(
    (tab: OverviewTabKey): void => {
      setSearchParams({ tab });
    },
    [setSearchParams],
  );

  // Flush the client-side JS cache and bump the version so all hooks re-fetch.
  const handleFlushCache = useCallback((): void => {
    dataCache.clear();
    setCacheVersion((v) => v + 1);
  }, []);

  // Only whyellow/administrator roles get the refresh button.
  const canFlushCache =
    keycloak.hasRealmRole(AppConstants.WHYELLOW_ROLE);

  const refreshCacheButton = canFlushCache ? (
    <button type="button" className={style(surfaceStyles.ghostButton)} title={t('refresh-cache')?.toString()} onClick={handleFlushCache}>
      <CachedIcon size={16} />
      {t('refresh-cache')}
    </button>
  ) : null;

  // The three lists load independently; showing the summary before they all arrive would count
  // down from zeros.
  const summaryIsReady = offices != null && parkings != null && facilities != null;

  // Anything not attached to an office can't be booked, so the page says so up front.
  const unlinkedCount =
    (parkings ?? []).filter((parking) => parking.officeIds.length === 0).length +
    (facilities ?? []).filter((facility) => facility.officeIds.length === 0).length;

  const summary = t('my-offices-summary')
    .replace('[x]', (offices?.length ?? 0).toString())
    .replace('[y]', (parkings?.length ?? 0).toString())
    .replace('[z]', (facilities?.length ?? 0).toString());

  const panelClass = (tabKey: OverviewTabKey): string => style(activeTab === tabKey ? undefined : tabStyles.hiddenPanel);

  return (
    <div className={style(CommonStyles.pageMargin)}>
      <div className={style(CommonStyles.bodyMargin(isMobile))}>
        <div className={style(listStyles.pageHead(isMobile))}>
          <div>
            <h1 className={style(listStyles.pageTitle)}>{t('my-offices')}</h1>
            <p className={style(listStyles.pageSub)}>
              {summaryIsReady && summary}
              {summaryIsReady && unlinkedCount > 0 && (
                <>
                  {' · '}
                  <span className={style(listStyles.pageSubWarning)}>
                    {t('not-linked-summary').replace('[x]', unlinkedCount.toString())}
                  </span>
                </>
              )}
            </p>
          </div>
          {refreshCacheButton}
        </div>

        <div className={style(tabStyles.tabBar(isMobile))} role="tablist">
          {OVERVIEW_TAB_KEYS.map((tabKey) => (
            <div
              key={tabKey}
              role="tab"
              tabIndex={0}
              aria-selected={activeTab === tabKey}
              className={style(tabStyles.tab(activeTab === tabKey))}
              onClick={(): void => setTab(tabKey)}
              onKeyDown={(event): void => {
                if (event.key === 'Enter' || event.key === ' ') {
                  setTab(tabKey);
                }
              }}
            >
              {t(tabKey)}
            </div>
          ))}
        </div>

        {visitedTabs.has('offices') && (
          <div className={panelClass('offices')}>
            <OfficeOverviewGroup
              allOffices={offices}
              allParkings={parkings}
              allFacilities={facilities}
            />
          </div>
        )}

        {visitedTabs.has('parkings') && (
          <div className={panelClass('parkings')}>
            <OfficeParkingsGroup allOffices={offices} allParkings={parkings} initialized={parkingState.initialized} />
          </div>
        )}

        {visitedTabs.has('facilities') && (
          <div className={panelClass('facilities')}>
            <OfficeFacilitiesGroup
              allFacilities={facilities}
              allOffices={offices}
              initialized={facilityState.initialized}
              readonly={false}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default OfficeOverview;
